import { Injectable, signal, computed } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';

/**
 * User state management service
 *
 * Manages user-related state that was previously handled by DatabaseCacheService.
 * Uses signals for reactive state management.
 */
@Injectable({ providedIn: 'root' })
export class UserStateService {
  /** Notifies subscribers when user-related data is updated */
  readonly userUpdated = new Subject<void>();

  /** Currently selected user ID */
  private readonly _selectedUserId = signal<string | null>(null);

  /** Users list (userWithUserData array) */
  private readonly _usersList = signal<UserWithUserData[]>([]);

  /** Section group admin assignments */
  private readonly _sectionGroupAdmins = signal<SectionGroupAdminAssignment[]>([]);

  /** System admin assignment */
  private readonly _systemAdmin = signal<SystemAdminAssignment | null>(null);

  // Public read-only signals
  readonly selectedUserId = this._selectedUserId.asReadonly();
  readonly usersList = this._usersList.asReadonly();
  readonly sectionGroupAdmins = this._sectionGroupAdmins.asReadonly();
  readonly systemAdmin = this._systemAdmin.asReadonly();

  // Observable versions for compatibility
  readonly selectedUserId$ = toObservable(this._selectedUserId);
  readonly usersList$ = toObservable(this._usersList);

  // Computed values
  readonly selectedUser = computed(() => {
    const userId = this._selectedUserId();
    if (!userId) return null;
    return this._usersList().find(u => u.user.userId === userId) ?? null;
  });

  readonly selectedUserName = computed(() => {
    const user = this.selectedUser();
    if (!user) return null;
    return user.user.code ?? `${user.user.lastName} ${user.user.firstName}`;
  });

  readonly hasSystemAdmin = computed(() => this._systemAdmin() !== null);

  // Setters
  setSelectedUserId(userId: string | null): void {
    this._selectedUserId.set(userId);
  }

  setUsersList(users: UserWithUserData[]): void {
    // Set userName for each user
    const usersWithUserName = users.map(user => ({
      ...user,
      user: {
        ...user.user,
        userName: user.user.code ?? `${user.user.lastName} ${user.user.firstName}`
      }
    }));
    this._usersList.set(usersWithUserName);
  }

  forceOverwriteUsersList(users: UserWithUserData[]): void {
    this.setUsersList(users);
  }

  setSectionGroupAdmins(admins: SectionGroupAdminAssignment[]): void {
    this._sectionGroupAdmins.set(admins ?? []);
  }

  setSystemAdmin(admin: SystemAdminAssignment | null): void {
    this._systemAdmin.set(admin);
  }

  // Query methods
  findUserById(userId: string): UserWithUserData | undefined {
    return this._usersList().find(u => u.user.userId === userId);
  }

  getUserName(userId: string): string | null {
    const user = this.findUserById(userId);
    if (!user) return null;
    return user.user.code ?? `${user.user.lastName} ${user.user.firstName}`;
  }

  // Section group admin role checks
  hasSectionGroupAdminRole(sectionGroupId: string, role: string): boolean {
    const admins = this._sectionGroupAdmins();
    const admin = admins.find(a => a.sectionGroupId === sectionGroupId);
    if (!admin) return false;
    return admin.roles.some(r => r.toUpperCase() === role.toUpperCase());
  }

  hasSectionManagement(sectionGroupId: string): boolean {
    return this.hasSectionGroupAdminRole(sectionGroupId, 'SECTION_MANAGEMENT');
  }

  hasSectionGroupUpdate(sectionGroupId: string): boolean {
    return this.hasSectionGroupAdminRole(sectionGroupId, 'SECTION_GROUP_UPDATE');
  }

  hasSectionGroupAdminManagement(sectionGroupId: string): boolean {
    return this.hasSectionGroupAdminRole(sectionGroupId, 'SECTION_GROUP_ADMIN_MANAGEMENT');
  }

  // System admin role checks
  hasSystemAdminRole(role: string): boolean {
    const systemAdmin = this._systemAdmin();
    if (!systemAdmin) return false;
    return systemAdmin.roles.some(r => r.toUpperCase() === role.toUpperCase());
  }

  hasAllSectionGroupManagement(): boolean {
    return this.hasSystemAdminRole('ALL_SECTION_GROUP_MANAGEMENT');
  }

  hasParameterUpdate(): boolean {
    return this.hasSystemAdminRole('PARAMETER_MANAGEMENT');
  }

  hasSectionGroupCreate(): boolean {
    return this.hasSystemAdminRole('SECTION_GROUP_CREATE');
  }

  hasSystemAdminManagement(): boolean {
    return this.hasSystemAdminRole('SYSTEM_ADMIN_MANAGEMENT');
  }

  hasQuotaManagement(): boolean {
    return this.hasSystemAdminRole('QUOTA_MANAGEMENT');
  }

  // Reset state
  reset(): void {
    this._selectedUserId.set(null);
    this._usersList.set([]);
    this._sectionGroupAdmins.set([]);
    this._systemAdmin.set(null);
  }
}

// Type definitions
export interface UserWithUserData {
  user: {
    userId: string;
    code?: string;
    lastName: string;
    firstName: string;
    userName?: string;
    [key: string]: unknown;
  };
  userData?: unknown;
  [key: string]: unknown;
}

export interface SectionGroupAdminAssignment {
  sectionGroupId: string;
  roles: string[];
  [key: string]: unknown;
}

export interface SystemAdminAssignment {
  roles: string[];
  [key: string]: unknown;
}
